import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CategoryTabs from './components/CategoryTabs';
import HotMovies from './components/HotMovies';
import LatestUpdate from './components/LatestUpdate';
import FeaturedCollections from './components/FeaturedCollections';
import MovieCard from '@/components/feature/MovieCard';
import { movies, faqData } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  const hotMovies = useMemo(() => movies.filter((m) => m.isHot), []);
  const newMovies = useMemo(() => movies.filter((m) => m.isNew).slice(0, 5), []);

  const filteredMovies = useMemo(() => {
    if (activeCategory === 'all') return movies.filter((m) => !m.isHot && !m.isNew).slice(0, 6);
    return movies.filter((m) => m.categorySlug === activeCategory).slice(0, 6);
  }, [activeCategory]);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '91精品视频在线播放 - 首页',
    description: '精选高分电影和热门电视剧，助你告别片荒。每日更新精品推荐，全站高清画质流畅播放，必看影视一网打尽。',
    url: 'https://91jingpin.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '91精品视频在线播放',
      url: 'https://91jingpin.com/',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <HeroSection />
        <CategoryTabs
          activeSlug={activeCategory}
          onCategoryChange={(slug) => {
            setActiveCategory(slug);
          }}
        />

        {activeCategory === 'all' ? (
          <>
            <HotMovies movies={hotMovies} />
            <LatestUpdate movies={newMovies} />
            <FeaturedCollections />
          </>
        ) : (
          <section className="w-full px-4 md:px-6 py-10">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-6">
                {activeCategory === 'dongzuo' ? '动作片精选' :
                 activeCategory === 'xiju' ? '喜剧片精选' :
                 activeCategory === 'aiqing' ? '爱情片精选' :
                 activeCategory === 'kehuan' ? '科幻片精选' :
                 activeCategory === 'kongbu' ? '恐怖片精选' :
                 activeCategory === 'donghua' ? '动画片精选' :
                 activeCategory === 'jilupian' ? '纪录片精选' :
                 activeCategory === 'juqing' ? '剧情片精选' : '精选影片'}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {filteredMovies.map((movie: Movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="w-full px-4 md:px-6 py-10 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqData.map((faq, index) => (
                <details
                  key={index}
                  className="group rounded-lg bg-background-100 border border-background-200 overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">
                      {faq.question}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}